import React, { useMemo, useState, useEffect } from "react";
import { Text } from "react-native";
import Colors from "../../Resources/Color";

const TypingAnimation = ({
  texts,
  delay = 100,
  startDelay = 500,
  pauseBetween = 2000,
  enabled = true,
  disableOnWebKit = true,
}) => {
  const isWebKit = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const webkit = /AppleWebKit/i.test(ua);
    const isChromium = /Chrome|Chromium|Edg/i.test(ua);
    return webkit && !isChromium;
  }, []);

  const safeTexts = Array.isArray(texts) ? texts : [];
  if (!enabled) {
    return null;
  }

  // On WebKit (especially iOS Safari), frequent subtree updates inside/near a focused
  // textarea can trigger removeChild NotFoundError. Allow opting out of the animation.
  if (disableOnWebKit && isWebKit) {
    return (
      <Text
        style={{
          fontSize: 11,
          color: Colors.grayColor,
          textAlign: "left",
          lineHeight: 12,
        }}
      >
        {safeTexts[0] ?? ""}
      </Text>
    );
  }

  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [isPausing, setIsPausing] = useState(false);

  const currentText = safeTexts[currentTextIndex] ?? "";

  useEffect(() => {
    if (safeTexts.length === 0) return;
    // Start typing after initial delay
    const startTimer = setTimeout(() => {
      setIsTyping(true);
    }, startDelay);

    return () => clearTimeout(startTimer);
  }, [startDelay]);

  useEffect(() => {
    if (safeTexts.length === 0) return;
    if (!isTyping || isPausing) return;

    if (currentIndex >= currentText.length) {
      setIsPausing(true);
      return;
    }

    const timer = setTimeout(() => {
      setDisplayedText((prev) => prev + currentText[currentIndex]);
      setCurrentIndex((prev) => prev + 1);
    }, delay);

    return () => clearTimeout(timer);
  }, [currentIndex, currentText, delay, isTyping, isPausing]);

  useEffect(() => {
    if (safeTexts.length === 0) return;
    if (!isPausing) return;

    const pauseTimer = setTimeout(() => {
      setDisplayedText("");
      setCurrentIndex(0);
      setCurrentTextIndex((prev) => (prev + 1) % safeTexts.length);
      setIsPausing(false);
    }, pauseBetween);

    return () => clearTimeout(pauseTimer);
  }, [isPausing, pauseBetween, safeTexts.length]);

  useEffect(() => {
    // Cursor blink effect
    const cursorTimer = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);

    return () => clearInterval(cursorTimer);
  }, []);

  return (
    <Text
      style={{
        fontSize: 11,
        color: Colors.grayColor,
        textAlign: "left",
        lineHeight: 12,
      }}
    >
      {displayedText}
      <Text
        style={{
          color: Colors.grayColor,
          opacity: showCursor ? 1 : 0,
          fontSize: 12,
        }}
      >
        |
      </Text>
    </Text>
  );
};

export default TypingAnimation;
